import React, { useEffect, useState } from "react";
import { GoArrowRight } from "react-icons/go";
import { MdCheckCircle, MdErrorOutline } from "react-icons/md";
import Header from "../../components/Header";
import { useLocation, useNavigate } from "react-router-dom";
import ApiRequest from "../../auth/apiRequestHelper";
import { isValidVatNumber } from "../../utils/peppol";
import { useDispatch } from "react-redux";
import {
  setSelectedItemsClear,
  setSelectedUserId,
  setUserName,
  setWoshUserId,
  setSelectedUserEmail,
  setSelectedUserCompanyName,
  setSelectedUserInvoicingAddress,
  setSelectedUserPhone,
  setSelectedUserVat,
  setSelectedUserMonthlyInvoice,
} from "../../store/slices/cartSlice";
import { useTranslation } from "react-i18next";
import Loader from "react-loader-spinner";
import { WR } from "../../components/ui/tokens";

// VISUAL RELOOKING ONLY — same form state, same validation (incl. peppolMissing),
// same create/update API calls, dispatches and navigation as before.
const PersonalInfo = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { userName } = location.state || {};
  const { editUser } = location.state || false;
  const { retrieve } = location.state || false;
  const { id } = location.state || {};
  // Set by the PEPPOL toggle in checkout when billing info is incomplete.
  // When true, show an inline banner + red borders on VAT / Company Name /
  // Invoicing Address; block save until at least one of them is filled.
  const { peppolMissing } = location.state || {};
  const dispatch = useDispatch();

  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState(userName || "");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [vat, setVat] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [invoicingAddress, setInvoicingAddress] = useState("");
  const [customId, setCustomId] = useState("");
  const [firstNameError, setFirstNameError] = useState(false);
  const [lastNameError, setLastNameError] = useState(false);
  const [backendError, setBackendError] = useState(null);
  const [loading, setLoading] = useState(false);
  const lang = localStorage.getItem("lang") || "english";

  useEffect(() => {
    id &&
      ApiRequest.userFind(`shop/users/${id}`)
        .then(function (response) {
          // Coalesce nulls to "" — every text input below treats its value
          // as a string (e.g. vat.trim()). If the backend returns null on
          // an unset billing field, calling .trim() on it crashes the page
          // with "Cannot read properties of null (reading 'trim')".
          const u = response.data.user || {};
          setEmail(u.email ?? "");
          setFirstName(u.first_name ?? "");
          setLastName(u.last_name ?? "");
          setPhoneNumber(u.phone_number ?? "");
          setVat(u.vat ?? "");
          setCompanyName(u.company_name ?? "");
          setCustomId(u.wosh_user_id ?? "");
          setInvoicingAddress(u.invoicing_address ?? "");
        })
        .catch(function (error) {
          const errorMessage =
            error?.response?.data?.message ?? error.message ?? "Something went wrong";
          setBackendError(String(errorMessage).trim());
          setTimeout(() => { setBackendError(""); }, 2500);
        });
  }, []);

  const handleUserCreate = () => {
    setLoading(true);
    let hasError = false;

    if (firstName.trim() === "") {
      setFirstNameError(true);
      hasError = true;
    } else {
      setFirstNameError(false);
    }

    if (lastName.trim() === "") {
      setLastNameError(true);
      hasError = true;
    } else {
      setLastNameError(false);
    }

    // When opened via the PEPPOL toggle, require at least one billing field
    // AND reject malformed VAT numbers (the VAT format check applies broadly,
    // not just in PEPPOL mode, but blocking save here only when PEPPOL is the
    // entry point keeps legacy edit flows backwards compatible).
    if (peppolMissing) {
      if (vat.trim() !== "" && !isValidVatNumber(vat)) {
        setBackendError(t("PEPPOL_INVALID_VAT"));
        setTimeout(() => setBackendError(""), 5000);
        setLoading(false);
        return;
      }
      const anyBillingFilled =
        vat.trim() !== "" ||
        companyName.trim() !== "" ||
        invoicingAddress.trim() !== "";
      if (!anyBillingFilled) {
        setBackendError(t("PEPPOL_MISSING_INFO_BODY"));
        setTimeout(() => setBackendError(""), 4000);
        setLoading(false);
        return;
      }
    }

    if (hasError) {
      return;
    }
    let formData = new FormData();
    formData.append("user[email]", email);
    formData.append("user[first_name]", firstName);
    formData.append("user[last_name]", lastName);
    formData.append("user[phone_number]", phoneNumber);
    formData.append("user[custom_id]", customId);
    formData.append("user[vat]", vat);
    formData.append("user[company_name]", companyName);
    formData.append("user[invoicing_address]", invoicingAddress);
    formData.append(
      "user[locale]",
      lang === "english"
        ? "en"
        : lang === "french"
        ? "fr"
        : "nl"
    );

    if (editUser && id) {
      ApiRequest.userUpdate("shop/users", formData, id)
        .then(function (response) {
          setEmail("");
          setFirstName("");
          setLastName("");
          setPhoneNumber("");
          setCustomId("");
          dispatch(setSelectedUserId(response.data.user.id));
          dispatch(setSelectedUserPhone(response.data.user.phone_number));
          dispatch(setSelectedUserCompanyName(response.data.user.company_name));
          dispatch(setSelectedUserInvoicingAddress(response.data.user.invoicing_address));
          dispatch(setSelectedUserVat(response.data.user.vat));
          dispatch(setWoshUserId(response.data.user.wosh_user_id));
          dispatch(
            setUserName(
              response.data.user.first_name + " " + response.data.user.last_name
            )
          );
          dispatch(setSelectedUserEmail(response.data.user.email));
          dispatch(setSelectedUserMonthlyInvoice(response.data.user.monthly_invoice))
          // Carry the PEPPOL intent back to /retrivecheckout so the toggle
          // auto-arms when the user returns — saves them from having to
          // remember to tap PEPPOL again after fixing billing info.
          // /checkout no longer has the PEPPOL toggle so we don't pass it
          // there.
          if (retrieve) {
            navigate("/retrivecheckout", {
              state: {
                successMessage: "User updated successfully!!",
                ...(peppolMissing ? { peppolInvoiceIntended: true } : {}),
              },
            });
          } else {
            navigate("/checkout", {
              state: { successMessage: "User updated successfully!!" },
            });
          }
        })
        .catch(function (error) {
          const errorMessage = error?.response?.data?.message;
          if (errorMessage) {
            setBackendError(errorMessage.trim());
            setTimeout(() => {
              setBackendError("");
            }, 2500);
          } else {
            console.error("Error:", error.message);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      ApiRequest.userCreate("shop/users", formData)
        .then(function (response) {
          setEmail("");
          setFirstName("");
          setLastName("");
          setPhoneNumber("");
          setCustomId("");
          dispatch(setSelectedUserId(response.data.user.id));
          dispatch(setSelectedUserPhone(response.data.user.phone_number));
          dispatch(setSelectedUserCompanyName(response.data.user.company_name));
          dispatch(setSelectedUserInvoicingAddress(response.data.user.invoicing_address));
          dispatch(setSelectedUserVat(response.data.user.vat));
          dispatch(setWoshUserId(response.data.user.wosh_user_id));
          dispatch(
            setUserName(
              response.data.user.first_name + " " + response.data.user.last_name
            )
          );
          dispatch(setSelectedUserEmail(response.data.user.email));
          dispatch(setSelectedUserMonthlyInvoice(response.data.user.monthly_invoice))
          dispatch(setSelectedItemsClear());
          navigate("/");
        })
        .catch(function (error) {
          const errorMessage = error?.response?.data?.message;
          if (errorMessage) {
            const errorKeys = Object.keys(errorMessage);
            let combinedError = "";
            errorKeys.forEach((key) => {
              if (Array.isArray(errorMessage[key])) {
                const capitalizedKey =
                  key.charAt(0).toUpperCase() + key.slice(1); // Capitalize first letter of key
                const errorMessages = errorMessage[key].join(", ");
                combinedError += `${capitalizedKey} ${errorMessages} `;
              }
            });
            setBackendError(combinedError.trim());
          } else {
            console.error("Error:", error.message);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  // ---- presentational helpers (no behavior) ----
  const isRetrieve = !!retrieve;
  const accent = isRetrieve ? WR.color.mintDeep : WR.color.brand;
  const eyebrowBg = isRetrieve ? WR.color.accentSoft : "rgba(61, 69, 134, 0.08)";
  const flowLabel = isRetrieve ? t("DIRECTION_OUT") : t("DIRECTION_IN");
  const modeLabel = editUser ? t("EDIT_CUSTOMER") : t("NEW_CUSTOMER");
  const ready = !!(firstName && lastName) && !loading;

  const fieldLabel = {
    display: "block",
    fontSize: 13,
    fontWeight: 700,
    color: WR.color.inkMuted,
    marginBottom: 7,
  };
  const fieldStyle = (hasError) => ({
    width: "100%",
    height: 52,
    padding: "0 14px",
    borderRadius: 14,
    border: `1.5px solid ${hasError ? WR.color.danger : WR.color.border}`,
    background: "#fff",
    fontSize: 15,
    fontWeight: 600,
    color: WR.color.encre,
    outline: "none",
    fontFamily: WR.font.sans,
    boxSizing: "border-box",
  });
  const sectionHeader = {
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 1.4,
    textTransform: "uppercase",
    color: accent,
    margin: "0 0 16px",
  };
  const vatError =
    peppolMissing && (vat.trim() === "" || (vat.trim() !== "" && !isValidVatNumber(vat)));
  const companyError = peppolMissing && companyName.trim() === "";
  const invoicingError = peppolMissing && invoicingAddress.trim() === "";
  // Live VAT FORMAT feedback — same check as the billing-info modal. Shown
  // whenever the field is non-empty, regardless of the PEPPOL entry point.
  const vatTrimmedLive = (vat || "").trim();
  const vatLiveState = !vatTrimmedLive
    ? null
    : isValidVatNumber(vatTrimmedLive)
    ? "ok"
    : "bad";

  return (
    <>
      <Header />
      <div
        style={{
          background: WR.color.bg,
          minHeight: "calc(100vh - 67px)",
          fontFamily: WR.font.sans,
          padding: "clamp(20px,4vh,40px) clamp(16px,4vw,40px)",
        }}
      >
        <div style={{ maxWidth: 980, margin: "0 auto", width: "100%" }}>
          {/* eyebrow */}
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              padding: "6px 13px",
              borderRadius: 999,
              background: eyebrowBg,
              color: accent,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: 1.5,
              textTransform: "uppercase",
            }}
          >
            <span style={{ width: 7, height: 7, borderRadius: 999, background: accent }} />
            {flowLabel} · {modeLabel}
          </div>
          <h1 style={{ fontSize: "clamp(26px,3.2vw,34px)", fontWeight: 800, letterSpacing: -0.6, margin: "8px 0 24px", color: WR.color.encre }}>
            {t("CUSTOMER_DETAILS")}
          </h1>

          {backendError && (
            <div
              style={{
                padding: "12px 16px",
                borderRadius: 12,
                background: "#FDECEC",
                border: "1px solid #F3C9CF",
                color: WR.color.danger,
                fontSize: 14,
                fontWeight: 600,
                marginBottom: 18,
                textAlign: "center",
              }}
              role="alert"
            >
              {backendError}
            </div>
          )}

          {/* Card */}
          <div style={{ background: "#fff", borderRadius: 24, border: `1px solid ${WR.color.border}`, boxShadow: WR.shadow.card, padding: 28 }}>
            {/* Personal information */}
            <div style={sectionHeader}>{t("PERSONAL_INFORMATION")}</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 18 }}>
              <div>
                <label htmlFor="firstName" style={fieldLabel}>{t("FIRST_NAME")} *</label>
                <input
                  id="firstName"
                  type="text"
                  style={fieldStyle(firstNameError)}
                  value={firstName}
                  onChange={(e) => { setFirstName(e.target.value); setFirstNameError(false); }}
                  required
                />
              </div>
              <div>
                <label htmlFor="lastName" style={fieldLabel}>{t("LAST_NAME")} *</label>
                <input
                  id="lastName"
                  type="text"
                  style={fieldStyle(lastNameError)}
                  value={lastName}
                  onChange={(e) => { setLastName(e.target.value); setLastNameError(false); }}
                  required
                />
              </div>
              <div>
                <label htmlFor="email" style={fieldLabel}>{t("EMAIL")}</label>
                <input
                  id="email"
                  type="email"
                  style={fieldStyle(false)}
                  value={!email.match(/@test\.com$/) ? email : ""}
                  onChange={(e) => { setEmail(e.target.value); }}
                />
              </div>
              <div>
                <label htmlFor="phone" style={fieldLabel}>{t("PHONE")}</label>
                <input
                  id="phone"
                  type="tel"
                  style={fieldStyle(false)}
                  placeholder="+32 …"
                  value={phoneNumber}
                  onChange={(e) => { setPhoneNumber(e.target.value); }}
                />
              </div>
              <div>
                <label htmlFor="customId" style={fieldLabel}>{t("CUSTOM_ID")}</label>
                <input
                  id="customId"
                  type="text"
                  style={fieldStyle(false)}
                  value={customId}
                  onChange={(e) => { setCustomId(e.target.value); }}
                />
              </div>
            </div>

            {/* divider */}
            <div style={{ height: 1, background: WR.color.border, opacity: 0.7, margin: "26px 0 22px" }} />

            {/* Billing information */}
            <div style={sectionHeader}>{t("BILLING_INFORMATION")}</div>
            {peppolMissing && (
              <div
                role="alert"
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 8,
                  padding: "12px 16px",
                  background: "#FFF8E1",
                  borderLeft: "4px solid #F6AD55",
                  borderRadius: 10,
                  color: WR.color.encre,
                  fontSize: 13.5,
                  lineHeight: 1.5,
                  marginBottom: 18,
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F6AD55" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }} aria-hidden="true">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span>{t("PEPPOL_MISSING_INFO_BODY")}</span>
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 18 }}>
              <div>
                <label htmlFor="vat" style={fieldLabel}>{t("VAT")}</label>
                <input
                  id="vat"
                  type="text"
                  style={fieldStyle(vatError)}
                  value={vat}
                  onChange={(e) => { setVat(e.target.value); }}
                />
                {vatLiveState && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: 12,
                      marginTop: 6,
                      color: vatLiveState === "ok" ? "#177a44" : "#8a560f",
                    }}
                  >
                    {vatLiveState === "ok" ? (
                      <MdCheckCircle size={15} aria-hidden="true" />
                    ) : (
                      <MdErrorOutline size={15} aria-hidden="true" />
                    )}
                    {t(vatLiveState === "ok" ? "VAT_VALID" : "VAT_INVALID")}
                  </div>
                )}
              </div>
              <div>
                <label htmlFor="companyName" style={fieldLabel}>{t("COMPANY_NAME")}</label>
                <input
                  id="companyName"
                  type="text"
                  style={fieldStyle(companyError)}
                  value={companyName}
                  onChange={(e) => { setCompanyName(e.target.value); }}
                />
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <label htmlFor="invoicingAddress" style={fieldLabel}>{t("INVOICING_ADDRESS")}</label>
                <input
                  id="invoicingAddress"
                  type="text"
                  style={fieldStyle(invoicingError)}
                  value={invoicingAddress}
                  onChange={(e) => { setInvoicingAddress(e.target.value); }}
                />
              </div>
            </div>
          </div>

          {/* Save */}
          <div style={{ display: "flex", justifyContent: "center", marginTop: 28 }}>
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); handleUserCreate(); }}
              disabled={loading || !(firstName && lastName)}
              style={{
                height: 56,
                padding: "0 46px",
                borderRadius: 999,
                background: ready ? accent : "rgba(61, 69, 134, 0.22)",
                color: ready ? "#fff" : "rgba(0,0,0,0.45)",
                border: 0,
                fontSize: 16,
                fontWeight: 700,
                boxShadow: ready ? WR.shadow.cta : "none",
                display: "inline-flex",
                alignItems: "center",
                gap: 9,
                cursor: ready ? "pointer" : "not-allowed",
                fontFamily: WR.font.sans,
              }}
            >
              {editUser ? t("SAVE") : t("NEXT")}
              {loading ? (
                <Loader type="Oval" color="#ffffff" height={18} width={18} />
              ) : (
                <GoArrowRight size={18} />
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default PersonalInfo
